<template>
	<AppLayout :title="trans('assemblies.title')">
		<Modal v-if="canCreate" id="create-college-zone-period-modal">
			<template #title>{{ trans('assemblies.create.title') }}</template>
			<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
				<LabeledObject required full>
					<template #label>{{ trans('assemblies.nomination_starts_at') }}</template>
					<IconDateTimeInput v-model="newAssembly.nomination_starts_at" :min="min" icon-class="fa-solid fa-hourglass-start" full/>
				</LabeledObject>
				<LabeledObject required full>
					<template #label>{{ trans('assemblies.nomination_ends_at') }}</template>
					<IconDateTimeInput v-model="newAssembly.nomination_ends_at" :min="newAssembly.nomination_starts_at" icon-class="fa-solid fa-hourglass-end" full/>
				</LabeledObject>
				<Card class="md:col-span-2" :title="trans('assemblies.seats')">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
						<LabeledObject v-for="collegeZone in CollegeZone" required full>
							<template #label>{{ trans(`colleges.${collegeZone}`) }}</template>
							<NumberInput v-model="newAssembly[`${collegeZone}_seats`]"/>
						</LabeledObject>
					</div>
				</Card>
				<div>
					<Switch v-model="newAssembly.active" :label="trans('general.active')"/>
				</div>
				<div>
					<Switch v-model="createUrl" :label="trans('assemblies.create_url')"/>
				</div>
				<template v-if="createUrl">
					<LabeledObject required full>
						<template #label>{{ trans('assemblies.meeting_starts_at') }}</template>
						<IconDateTimeInput v-model="meetingStartsAt" icon-class="fa-solid fa-hourglass-start" full/>
					</LabeledObject>
					<LabeledObject required full>
						<template #label>{{ trans('assemblies.meeting_ends_at') }}</template>
						<IconDateTimeInput v-model="meetingEndsAt" icon-class="fa-solid fa-hourglass-end" full/>
					</LabeledObject>
				</template>
				<LabeledObject v-else class="md:col-span-2" required full>
					<template #label>{{ trans('assemblies.meeting_url') }}</template>
					<input v-model="meetingUrl" type="url">
				</LabeledObject>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="create">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton v-if="canCreate" :color="Color.Green" @click.stop="showModal('create-college-zone-period-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Empty v-if="assemblies.length === 0"/>
		<CardGrid v-else>
			<Card v-for="assembly in assemblies" :key="`assembly-${assembly.id}`" :title="`${dayjs(assembly.nomination_starts_at).format(trans('general.dayjs.date'))} - ${dayjs(assembly.nomination_ends_at).format(trans('general.dayjs.date'))}`">
				<template #badges>
					<Badge v-if="assembly.active" :color="Color.Green" active>{{ trans('general.active') }}</Badge>
					<Badge v-else :color="Color.Red">{{ trans('general.inactive') }}</Badge>
				</template>
				<div class="grid grid-cols-2 gap-3">
					<LabeledObject v-for="collegeZone in CollegeZone">
						<template #label>{{ trans(`colleges.${collegeZone}`) }}</template>
						<p>{{ assembly[`${collegeZone}_seats`] }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('assemblies.meeting_url') }}</template>
						<a :href="assembly.meeting.link" class="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400" target="_blank">
							<i class="fa-solid fa-external-link mr-2"/>
							<span>{{ trans('general.follow_link') }}</span>
						</a>
					</LabeledObject>
				</div>
				<template v-if="canUpdate" #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('association.assemblies.edit', assembly.id)">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {ref, type Ref} from 'vue'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {CollegeZone} from '@/Types/Enums/CollegeZone'
import Switch from '@/Components/UI/Switches/Switch.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	canCreate:boolean,
	canUpdate:boolean,
	assemblies:Array<Assembly>,
}>()

const newAssembly:Ref<Assembly> = ref({
	nw_seats: 2,
	ne_seats: 2,
	cc_seats: 2,
	sp_seats: 2,
	gba_seats: 2,
	nomination_starts_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
	nomination_ends_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
	active: false,
} as Assembly)
const assemblies:Ref<Array<Assembly>> = ref(props.assemblies)
const canCreate:Ref<boolean> = ref(props.canCreate)
const createUrl:Ref<boolean> = ref(true)
const meetingStartsAt:Ref<string|null> = ref(null)
const meetingEndsAt:Ref<string|null> = ref(null)
const meetingUrl:Ref<string|null> = ref(null)
const working:Ref<boolean> = ref(false)
const min = dayjs().format('YYYY-MM-DD')

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.assemblies.store'), {
			...newAssembly.value,
			create_url: createUrl.value,
			meeting_url: meetingUrl.value,
			meeting_starts_at: meetingStartsAt.value,
			meeting_ends_at: meetingEndsAt.value,
		}).then(({data}:{data:Assembly}) => {
			showSuccess(trans('general.inserted'))
			closeModal('create-college-zone-period-modal')
			canCreate.value = false
			assemblies.value.unshift(data)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>